import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { ApiError } from '@repo/api-client';
import { fetcher } from '../../lib/api-fetcher';
import {
  TWO_FACTOR_STEP_UP_EVENT,
  resolveTwoFactorStepUp,
} from '../../lib/two-factor-step-up';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { Button } from './Button';
import { FormField, Input } from './FormField';

/**
 * Asks for a TOTP code when the API says a route needs a fresh in-session proof
 * (`TWO_FACTOR_STEP_UP_REQUIRED`) instead of a new session. Mounted once in the authed
 * layout; the request that triggered it replays itself after verification succeeds.
 */
export function TwoFactorStepUpDialog() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onChallenge = () => setOpen(true);
    window.addEventListener(TWO_FACTOR_STEP_UP_EVENT, onChallenge);
    return () => window.removeEventListener(TWO_FACTOR_STEP_UP_EVENT, onChallenge);
  }, []);

  const close = (verified: boolean) => {
    setOpen(false);
    setCode('');
    setError(null);
    resolveTwoFactorStepUp(verified);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || code.length !== 6) return;
    setSubmitting(true);
    setError(null);
    try {
      // skipAuthRedirect: a bad code must surface as a field error, not a refresh/logout dance.
      await fetcher.post('/auth/2fa/step-up', { code }, { skipAuthRedirect: true });
      close(true);
      // allow-silent-catch: rendered as an inline field message; the dialog stays open for retry.
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed. Try again.');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !submitting) close(false);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent-600" aria-hidden="true" />
            Two-factor verification
          </DialogTitle>
          <DialogDescription>
            This action needs a fresh authenticator code. Enter the 6-digit code from your
            authenticator app to continue.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label="Authenticator code" required error={error ?? undefined} htmlFor="step-up-code">
            <Input
              id="step-up-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={submitting}
              error={!!error}
              autoFocus
            />
          </FormField>

          <DialogFooter>
            <Button type="button" variant="secondary" disabled={submitting} onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={code.length !== 6}>
              Verify
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
